import type { FunctionComponent } from 'react'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, FormField, Heading, InputV2, TypographyV2 as Typography } from '@which/seatbelt'
import type { InputTypes } from '@which/seatbelt/build/src/components/Seatbelt2/BuildingBlocks/Forms/Input/Input'

import { Link } from '../../Link'
import { NewsletterLoggedInUserText } from '../NewsletterLoggedInUserText'
import styles from './NewsletterSignUpForm.module.scss'

export const NewsletterSignUpForm: FunctionComponent<NewsletterFormProps> = ({
  onFocus,
  onSignUp,
  buttonText,
  withAllFields,
  description,
  unsubscribeText,
  header,
  errorMsg,
  loading,
  email,
}) => {
  const { control, handleSubmit } = useForm({ mode: 'onBlur' })

  const renderField = ({
    field,
    label,
    inputType = 'text' as InputTypes,
    required = true,
    tooltip = '',
    validation,
    autoComplete,
  }) => {
    const optionalEventHandlers =
      typeof onFocus === 'function' && field === 'email' ? { onFocus } : ({} as const)
    return (
      <Controller
        control={control}
        name={field}
        defaultValue=""
        rules={validation}
        render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => {
          const message = error ? `Please enter a valid ${label}` : undefined

          return (
            <FormField
              label={label}
              labelFor={field}
              className={styles[field]}
              tooltip={tooltip}
              required={required}
              {...{ errorMsg: message }}
            >
              <InputV2
                inputRef={ref as unknown as React.RefObject<HTMLInputElement>}
                aria-invalid={!!error}
                name={field}
                type={inputType}
                value={value}
                {...optionalEventHandlers}
                onChange={onChange}
                onBlur={onBlur}
                className={styles[`${field}-input`]}
                autoComplete={autoComplete}
              />
            </FormField>
          )
        }}
      />
    )
  }

  const SubmitButton = (
    <Button
      className={styles.signUpButton}
      type="submit"
      data-testid="sign-up-button"
      disabled={loading}
      enableSpinner={loading}
      onClick={handleSubmit((data) => {
        if (email) {
          onSignUp({ email })
        } else {
          onSignUp(data)
        }
      })}
    >
      {buttonText}
    </Button>
  )

  const fields = withAllFields ? (
    <>
      <div className={styles.newsletterFormName}>
        {renderField({
          field: 'firstName',
          label: 'First name',
          autoComplete: 'given-name',
          validation: { required: true },
        })}
        {renderField({
          field: 'lastName',
          label: 'Last name',
          autoComplete: 'family-name',
          validation: { required: true },
        })}
      </div>
      {renderField({
        field: 'email',
        label: 'Email address',
        inputType: 'email',
        autoComplete: 'email',
        validation: {
          required: true,
          type: 'email',
          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        },
      })}
      {renderField({
        field: 'postcode',
        label: 'Postcode',
        autoComplete: 'postal-code',
        required: false,
        tooltip:
          "We use postcode data to help us understand who uses our services. You do not have to provide this to us, but we're including it because we'd like to reach as wide a range of people as possible and by obtaining this information we can ensure that we're doing this.",
        validation: {
          pattern: /(gir\s*0aa)|^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i,
        },
      })}
    </>
  ) : (
    <div className={styles.newsletterFormEmailForm}>
      {renderField({
        field: 'email',
        label: 'Email address',
        autoComplete: 'email',
        inputType: 'email',
        validation: {
          required: true,
          type: 'email',
          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        },
      })}
    </div>
  )

  return (
    <div
      className={`${styles.newsletterForm} ${withAllFields ? styles.allFields : styles.emailOnly}`}
      data-testid="newsletter-signup-form"
    >
      <div className={styles.newsletterFormHeaderContainer}>
        <Heading heading={header} headingTag="h2" headingType="large" />
        <Typography className={styles.newsletterFormOverline} textStyle="sb-text-heading-overline">
          free newsletter
        </Typography>
        <Typography
          className={styles.newsletterFormDescription}
          textStyle="sb-text-body-default-regular"
        >
          {description}
        </Typography>
      </div>
      <form method="post">
        {email ? (
          <NewsletterLoggedInUserText className={styles.loggedInUserText} email={email} />
        ) : (
          fields
        )}
        {SubmitButton}
      </form>
      {errorMsg && (
        <span className={styles.submitError} data-testid="submit-error">
          {errorMsg}
        </span>
      )}
      {unsubscribeText ? (
        <div className={styles.newsletterFormUnsubscribe}>{unsubscribeText}</div>
      ) : (
        <Typography
          textStyle="sb-text-interface-body-x-small-regular"
          className={styles.newsletterFormUnsubscribe}
        >
          Unsubscribe whenever you want. Your data will be processed in accordance with our{' '}
          <Link
            href="https://www.which.co.uk/help/our-policies-and-standards-aIbGY5S5YFzV"
            target="_blank"
            textStyle="sb-text-interface-body-x-small-regular"
          >
            Privacy notice
          </Link>
          .
        </Typography>
      )}
    </div>
  )
}

export type NewsletterFormProps = {
  onFocus?: () => void
  onSignUp: (NewsletterFormData) => void
  buttonText?: string
  withAllFields?: boolean
  description: string | JSX.Element | undefined
  additionalText?: JSX.Element
  unsubscribeText?: JSX.Element
  header: string
  errorMsg?: string
  loading: boolean
  email?: string
}
